import { session } from './session';
import { payment } from './payment';
import {
  ICreateSessionRequest,
  ICreateSessionResponse,
  ISessionData,
  IUpdateSessionRequest,
  IUpdateSessionResponse,
  IPaymentData,
  ICheckoutDataResponse,
  IGooglePayConfig,
  IMerchantPaymentUrlResponse,
  IEstimatePaymentResponse,
} from './types';

/**
 * CAP Payment Service
 * Main service class for CAP payment integration
 * Provides a unified interface for all CAP payment operations
 */
export default class CapService {
  private env: string;

  constructor(env: string) {
    this.env = env;
  }

  // Session Operations
  
  /**
   * Creates a new payment session
   * 
   * @param data - Session creation parameters
   * @returns Session ID
   */
  public async createSession(data: ICreateSessionRequest): Promise<ICreateSessionResponse> {
    return await session.createSession(data, this.env);
  }

  /**
   * Retrieves current session data
   * 
   * @param sessionId - Session identifier
   * @returns Complete session data
   */
  public async getSession(sessionId: string): Promise<ISessionData> {
    return await session.getSession(sessionId, this.env);
  }

  /**
   * Updates session information
   * 
   * @param sessionId - Session identifier
   * @param data - Data to update
   * @returns Success status
   */
  public async updateSession(
    sessionId: string,
    data: IUpdateSessionRequest
  ): Promise<IUpdateSessionResponse> {
    return await session.updateSession(sessionId, data, this.env);
  }

  // Payment Operations

  /**
   * Retrieves checkout data for payment processing
   * 
   * @param sessionId - Session identifier
   * @returns Checkout data including integrity token
   */
  public async getCheckoutData(sessionId: string): Promise<ICheckoutDataResponse> {
    return await payment.getCheckoutData(sessionId, this.env);
  }

  /**
   * Retrieves payment details by checkout ID
   * 
   * @param checkoutId - Checkout identifier
   * @param sessionId - Session identifier
   * @returns Payment information
   */
  public async getPaymentByCheckoutId(
    checkoutId: string,
    sessionId: string
  ): Promise<IPaymentData> {
    return await payment.getPaymentByCheckoutId(checkoutId, sessionId, this.env);
  }

  /**
   * Retrieves Google Pay configuration
   * 
   * @param sessionId - Session identifier
   * @returns Google Pay configuration
   */
  public async getGooglePayConfig(sessionId: string): Promise<IGooglePayConfig> {
    return await payment.getGooglePayConfig(sessionId, this.env);
  }

  /**
   * Generates merchant payment URL
   * 
   * @param sessionId - Session identifier
   * @returns External URL for merchant widget
   */
  public async generateMerchantPaymentUrl(sessionId: string): Promise<IMerchantPaymentUrlResponse> {
    return await payment.generateMerchantPaymentUrl(sessionId, this.env);
  }

  // Widget URL Helpers

  /**
   * Gets the payment widget URL
   * 
   * @param sessionId - Session identifier
   * @param checkoutId - Checkout identifier from session update
   * @param redirectUrl - URL to redirect after payment
   * @param theme - Site theme (dark/light)
   * @returns Widget iframe URL
   */
  public getWidgetUrl(sessionId: string, checkoutId: string, redirectUrl: string, theme?: string): string {
    return payment.getWidgetUrl(sessionId, checkoutId, redirectUrl, theme, this.env);
  }

  /**
   * Gets the merchant widget URL
   * 
   * @param sessionId - Session identifier
   * @returns Merchant widget iframe URL
   */
  public getMerchantWidgetUrl(sessionId: string): string {
    return payment.getMerchantWidgetUrl(sessionId, this.env);
  }

  // Estimate Operations

  /**
   * Estimates payment conversion
   * 
   * @param paymentRouteId - Payment route identifier
   * @param amount - Amount to convert
   * @returns Estimate payment data
   */
  public async estimatePayment(paymentRouteId: string, amount: string): Promise<IEstimatePaymentResponse> {
    return await payment.estimatePayment(paymentRouteId, amount, this.env);
  }

  // Payment Status

  /**
   * Gets payment status by checkout ID
   * 
   * @param sessionId - Session identifier
   * @param checkoutId - CAP checkout identifier
   * @returns Payment status data
   */
  public async getPaymentStatus(sessionId: string, checkoutId: string): Promise<IPaymentData> {
    return await payment.getPaymentStatus(sessionId, checkoutId, this.env);
  }
}

